import {
  useAllInvestmentAccountsQuery,
  useCreateInvestmentAdjustmentMutation,
  useDeleteInvestmentAdjustmentMutation,
  useInvestmentAdjustmentsQuery,
  useInvestmentGapsQuery,
} from '@transactions/graphql';
import {ChevronRight} from 'lucide-react';
import * as React from 'react';
import {Button} from 'src/Button';
import {formatShares, formatSigned} from 'src/InvestmentAccountsView/money';
import {
  confidentAdjustment,
  isShareGap,
  RECONCILE_REFETCH,
  suggestedChoice,
  visibleGaps,
} from 'src/InvestmentAccountsView/reconcileModel';
import {cn} from 'src/lib/utils';
import {useModals} from 'src/Modals';
import type {Gap} from 'src/InvestmentAccountsView/reconcileModel';

const sizeOf = (gap: Gap, currency: string) => {
  if (!isShareGap(gap)) return formatSigned(gap.cash ?? 0, currency);
  const shares = formatShares(gap.quantity ?? 0);
  // A paired trade carries its cash.
  return gap.suggestion.cash != null ? `${shares} · ${formatSigned(gap.suggestion.cash, currency)}` : shares;
};

const GapCard = ({
  gap,
  accountName,
  currency,
  undoing,
  onRecord,
  onUndo,
  onOpen,
  busy,
}: {
  gap: Gap;
  accountName: string;
  currency: string;
  /** The note of the adjustment the feed duplicated, when the suggestion is to delete it. */
  undoing: string | null;
  onRecord: () => void;
  onUndo: () => void;
  onOpen: () => void;
  busy: boolean;
}) => {
  const choice = suggestedChoice(gap);
  const duplicate = gap.suggestion.undoAdjustmentId != null;
  return (
    <div className="border-border bg-card flex flex-col gap-2.5 rounded-lg border p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate font-medium">{gap.securityLabel ?? 'Cash'}</div>
          <div className="text-muted-foreground truncate text-sm">{accountName}</div>
        </div>
        <span className="shrink-0 font-semibold tabular-nums">{sizeOf(gap, currency)}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span
          className={cn(
            'rounded-full px-2 py-0.5',
            gap.status === 'settling' ? 'bg-muted text-muted-foreground' : 'bg-warning/15 text-warning',
          )}
        >
          {gap.status === 'settling' ? 'Settling' : `Since ${gap.since}`}
        </span>
        {duplicate ? (
          <span className="text-muted-foreground">Already in the feed</span>
        ) : (
          gap.suggestion.confident && (
            <span className="text-muted-foreground">Looks like {choice.label.toLowerCase()}</span>
          )
        )}
      </div>
      {gap.suggestion.evidence != null && (
        <div className="text-muted-foreground text-sm">↳ {gap.suggestion.evidence}</div>
      )}
      <div className="flex flex-wrap gap-2">
        {duplicate ? (
          <>
            <Button variant="primary" disabled={busy || undoing == null} onClick={onUndo}>
              Delete recorded {(undoing === '' || undoing == null ? 'adjustment' : undoing).toLowerCase()}
            </Button>
            <Button variant="outline" disabled={busy} onClick={onOpen}>
              Something else
            </Button>
          </>
        ) : gap.suggestion.confident ? (
          <>
            <Button variant="primary" disabled={busy} onClick={onRecord}>
              {choice.kind === 'writeoff' ? 'Write off' : `Record ${choice.label.toLowerCase()}`}
            </Button>
            <Button variant="outline" disabled={busy} onClick={onOpen}>
              Something else
            </Button>
          </>
        ) : (
          <Button variant="primary" disabled={busy} onClick={onOpen}>
            What was this?
          </Button>
        )}
      </div>
    </div>
  );
};

/**
 * The Reconcile tab: the checks' open gaps, what needs a look first and
 * what's still settling folded away, each recorded in one step when the
 * evidence is strong or through "What was this?"; then the adjustments
 * made, which can be deleted.
 */
export const InvestmentReconcileView = () => {
  const modals = useModals();
  // Any refetch (a record's, a sync's, a retry) shows as loading, which holds the cards.
  const {data, loading, error, refetch} = useInvestmentGapsQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const {
    data: adjustmentsData,
    loading: adjustmentsLoading,
    error: adjustmentsError,
    refetch: refetchAdjustments,
  } = useInvestmentAdjustmentsQuery({fetchPolicy: 'cache-and-network', notifyOnNetworkStatusChange: true});
  // The accounts give the gaps their currencies, so they're refreshed alongside.
  const {
    data: accountsData,
    loading: accountsLoading,
    error: accountsError,
    refetch: refetchAccounts,
  } = useAllInvestmentAccountsQuery({fetchPolicy: 'cache-and-network', notifyOnNetworkStatusChange: true});
  const [create, {loading: recording}] = useCreateInvestmentAdjustmentMutation(RECONCILE_REFETCH);
  const [remove, {loading: removing}] = useDeleteInvestmentAdjustmentMutation(RECONCILE_REFETCH);
  const [recorded, setRecorded] = React.useState<{id: string; label: string} | null>(null);
  const [failed, setFailed] = React.useState<string | null>(null);

  const accounts = new Map((accountsData?.allInvestmentAccounts ?? []).map((account) => [account._id, account]));
  const nameOf = (accountId: string) => accounts.get(accountId)?.name ?? 'Account';
  const currencyOf = (accountId: string) => accounts.get(accountId)?.currency ?? 'USD';
  // Retries whichever of the gaps and accounts failed (a retry that fails
  // leaves its error, and the cards still held off).
  const retry = () => {
    if (error != null) void refetch().catch(() => undefined);
    if (accountsError != null) void refetchAccounts().catch(() => undefined);
  };

  // Amounts are in their account's currency, so nothing shows until the accounts are in.
  if ((loading && data == null) || (accountsLoading && accountsData == null)) {
    return <div className="text-muted-foreground p-8 text-center">Loading gaps...</div>;
  }
  if ((data == null && error != null) || (accountsData == null && accountsError != null)) {
    return (
      <div className="text-muted-foreground flex flex-col items-center gap-2 p-8 text-center text-sm">
        Couldn&apos;t load gaps.
        <Button variant="outline" onClick={retry}>
          Retry
        </Button>
      </div>
    );
  }

  // History that's refreshing, or failed to, may list what's already
  // deleted: nothing deletes until it's fresh.
  const adjustmentsStale = adjustmentsLoading || adjustmentsError != null;
  // Gaps (and the accounts that give them their currencies) that are
  // refreshing, or failed to, may already be closed or changed (by a record
  // or a sync): nothing records, and nothing claims all's balanced, until
  // they're fresh.
  const stale = error != null || accountsError != null;
  const fresh = !stale && !loading && !accountsLoading;
  const gaps = visibleGaps(data?.investmentGaps ?? []);
  const needs = gaps.filter((gap) => gap.status === 'unbalanced');
  const settling = gaps.filter((gap) => gap.status === 'settling');
  const adjustments = [...(adjustmentsData?.investmentAdjustments ?? [])].sort((a, b) => b.date.localeCompare(a.date));

  const open = (gap: Gap) =>
    modals.reconcileGapModal.openModal({
      gap,
      onRecorded: (id, label) => {
        setFailed(null);
        setRecorded({id, label});
      },
    });
  const record = async (gap: Gap) => {
    setFailed(null);
    try {
      const result = await create({variables: confidentAdjustment(gap)});
      const id = result.data?.createInvestmentAdjustment._id;
      if (id != null) setRecorded({id, label: suggestedChoice(gap).label});
    } catch (err) {
      setFailed(err instanceof Error ? err.message : "Couldn't record it");
      // Refused, maybe because the gap has moved on (another tab, a sync):
      // fetch it as it is now. Only a success refetches on its own.
      void refetch().catch(() => undefined);
    }
  };
  // An adjustment stays (and so does its Undo) until the delete succeeds.
  // Deleted as a gap's duplicate, it names the gap: the server refuses it
  // if that gap no longer suggests it.
  const unrecord = async (adjustmentId: string, gap?: Gap) => {
    setFailed(null);
    try {
      await remove({variables: {adjustmentId, gapSince: gap?.since, gapDay: gap?.day}});
      setRecorded((current) => (current?.id === adjustmentId ? null : current));
    } catch (err) {
      setFailed(err instanceof Error ? err.message : "Couldn't delete it");
      // Refused, maybe because the gap has moved on or the adjustment is
      // already gone (another tab): fetch both as they are now.
      if (gap != null) {
        void refetch().catch(() => undefined);
        void refetchAdjustments().catch(() => undefined);
      }
    }
  };
  const noteOf = (adjustmentId: string | null | undefined) =>
    adjustmentsStale ? null : (adjustments.find((adjustment) => adjustment._id === adjustmentId)?.note ?? null);
  const card = (gap: Gap) => (
    <GapCard
      key={`${gap.accountId} ${gap.securityId ?? 'cash'}`}
      gap={gap}
      accountName={nameOf(gap.accountId)}
      currency={currencyOf(gap.accountId)}
      undoing={noteOf(gap.suggestion.undoAdjustmentId)}
      busy={recording || removing || !fresh || !accounts.has(gap.accountId)}
      onRecord={() => void record(gap)}
      onUndo={() => {
        const adjustmentId = gap.suggestion.undoAdjustmentId;
        if (adjustmentId != null) void unrecord(adjustmentId, gap);
      }}
      onOpen={() => open(gap)}
    />
  );

  return (
    <div className="flex flex-1 flex-col overflow-y-auto">
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-3 p-4">
        {recorded != null && (
          <div className="bg-primary text-primary-foreground flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm">
            <span>{recorded.label} recorded</span>
            <button
              type="button"
              className="underline"
              disabled={removing || adjustmentsStale}
              onClick={() => void unrecord(recorded.id)}
            >
              Undo
            </button>
          </div>
        )}
        {failed != null && (
          <div role="alert" className="bg-destructive/10 text-destructive rounded-lg px-3 py-2 text-sm">
            {failed}
          </div>
        )}
        {stale && (
          <div
            role="alert"
            className="bg-destructive/10 text-destructive flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm"
          >
            <span>Couldn&apos;t refresh gaps.</span>
            <button type="button" className="underline" onClick={retry}>
              Retry
            </button>
          </div>
        )}
        {gaps.length === 0 && fresh && (
          <div className="border-border bg-card text-muted-foreground rounded-lg border p-8 text-center">
            <div className="text-positive text-2xl">✓</div>
            All balanced
          </div>
        )}
        {needs.length > 0 && (
          <>
            <h2 className="text-muted-foreground px-1 text-xs tracking-wide uppercase">Needs a look</h2>
            {needs.map(card)}
          </>
        )}
        {settling.length > 0 && (
          <details className="group border-border bg-card rounded-lg border">
            <summary className="flex cursor-pointer list-none items-center justify-between px-3 py-2.5 [&::-webkit-details-marker]:hidden">
              <span className="flex items-center gap-1.5">
                <ChevronRight className="text-muted-foreground size-4 transition-transform group-open:rotate-90" />
                Settling
              </span>
              <span className="text-muted-foreground">{settling.length}</span>
            </summary>
            <div className="flex flex-col gap-2 p-2">{settling.map(card)}</div>
          </details>
        )}
        {adjustmentsError != null && (
          <div
            role="alert"
            className="border-border bg-card text-destructive flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-sm"
          >
            <span>
              {adjustmentsData == null ? "Couldn't load adjustments." : "Couldn't refresh adjustments."}
            </span>
            <button
              type="button"
              className="underline"
              onClick={() => void refetchAdjustments().catch(() => undefined)}
            >
              Retry
            </button>
          </div>
        )}
        {adjustments.length > 0 && (
          <details className="group border-border bg-card rounded-lg border">
            <summary className="flex cursor-pointer list-none items-center justify-between px-3 py-2.5 [&::-webkit-details-marker]:hidden">
              <span className="flex items-center gap-1.5">
                <ChevronRight className="text-muted-foreground size-4 transition-transform group-open:rotate-90" />
                Adjustments
              </span>
              <span className="text-muted-foreground">{adjustments.length}</span>
            </summary>
            <div className="flex flex-col">
              {adjustments.map((adjustment) => (
                <div
                  key={adjustment._id}
                  className="border-border flex items-center justify-between gap-3 border-t px-3 py-2 text-sm"
                >
                  <div className="min-w-0">
                    <div className="truncate">
                      {[adjustment.kind, adjustment.securityLabel, nameOf(adjustment.accountId)].filter(Boolean).join(' · ')}
                    </div>
                    <div className="text-muted-foreground truncate text-xs">
                      {adjustment.date}
                      {adjustment.note ? ` · ${adjustment.note}` : ''}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="tabular-nums">
                      {/* Cash alone is in the currency it was recorded in; a trade's
                          own currency is its shares', so its cash is the account's. */}
                      {adjustment.cash != null
                        ? formatSigned(
                            adjustment.cash,
                            adjustment.quantity == null ? adjustment.currency : currencyOf(adjustment.accountId),
                          )
                        : ''}
                      {adjustment.quantity != null ? ` ${formatShares(adjustment.quantity)}` : ''}
                    </span>
                    <Button variant="ghost" disabled={removing || adjustmentsStale} onClick={() => void unrecord(adjustment._id)}>
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    </div>
  );
};
